import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Database, Sparkles, BookOpen, Plus, Trash2, ExternalLink, Webhook, ArrowRight, Copy, Check } from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import Drawer from '../../components/ui/Drawer';
import { SUPPORTED_TIMEZONES } from '../../contexts/TimezoneContext';
import { sourcesApi, type OverviewSource } from '../../services/sources-api';
import DeletionImpactPreview, { type ImpactItem } from '../../components/settings/DeletionImpactPreview';
import { accessApi } from '../../services/access-api';
import { teamApi } from '../../services/team-api';
import { schedulesApi } from '../../services/schedules-api';
import { orgApi } from '../../services/org-api';
import { handleApiError } from '../../services/api';
import { integrationsApi } from '../../services/integrations-api';
import { usePermissions } from '../../contexts/AuthContext';
import { integrationDisplay, setupKind } from './sourceDisplay';
import Toggle from './Toggle';
import type { DrawerTab } from './SourceRow';

interface SourceSettingsDrawerProps {
  source: OverviewSource | null;
  orgId: string | null;
  orgName: string | null;
  initialTab: DrawerTab;
  onClose: () => void;
}

function tabsFor(source: OverviewSource): Array<{ id: DrawerTab; label: string }> {
  const tabs: Array<{ id: DrawerTab; label: string }> = [{ id: 'general', label: 'General' }];
  // Setup only appears when a source actually has something to configure — no
  // empty tab for Plaid and friends.
  if (setupKind(source.sourceType)) tabs.push({ id: 'setup', label: 'Setup' });
  tabs.push(
    { id: 'accounts', label: 'Accounts' },
    { id: 'access', label: 'Access' },
    { id: 'danger', label: 'Danger zone' },
  );
  return tabs;
}

const SourceSettingsDrawer: React.FC<SourceSettingsDrawerProps> = ({
  source,
  orgId,
  orgName,
  initialTab,
  onClose,
}) => {
  const [tab, setTab] = useState<DrawerTab>(initialTab);
  useEffect(() => setTab(initialTab), [initialTab, source?.id]);

  const display = source ? integrationDisplay(source) : null;
  const tabs = source ? tabsFor(source) : [];
  // Fall back to General if the requested tab doesn't exist for this source.
  const activeTab: DrawerTab = tabs.some((t) => t.id === tab) ? tab : 'general';

  return (
    <Drawer isOpen={!!source} onClose={onClose} width="md">
      {source && display && (
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center gap-3 px-5 pt-4 pb-3.5 border-b border-border">
            <div className="w-[34px] h-[34px] rounded-[8px] overflow-hidden flex items-center justify-center bg-muted flex-none">
              {display.logo ? (
                <img src={display.logo} alt="" className="w-[34px] h-[34px] object-contain" />
              ) : (
                <Database className="w-4 h-4 text-muted-foreground" />
              )}
            </div>
            <div className="min-w-0">
              <div className="text-[15px] font-semibold text-foreground truncate">{source.name}</div>
              <div className="text-xs text-muted-foreground truncate">
                {display.name} · {source.mode === 'ingestion' ? 'Stored copy' : 'On-demand'}
                {orgName && ` · ${orgName}`}
              </div>
            </div>
            <button
              onClick={onClose}
              className="ml-auto w-[30px] h-[30px] rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-0.5 px-5 border-b border-border">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={clsx(
                  'px-3 py-2.5 text-[13px] font-medium border-b-2 -mb-px',
                  activeTab === t.id
                    ? 'text-primary border-primary font-semibold'
                    : 'text-muted-foreground border-transparent hover:text-foreground',
                )}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-5">
            {activeTab === 'general' && <GeneralTab source={source} orgId={orgId} orgName={orgName} onClose={onClose} />}
            {activeTab === 'setup' && <SetupTab source={source} />}
            {activeTab === 'accounts' && <AccountsTab source={source} />}
            {activeTab === 'access' && <AccessTab source={source} />}
            {activeTab === 'danger' && <DangerTab source={source} onClose={onClose} />}
          </div>
        </div>
      )}
    </Drawer>
  );
};

// ── General ─────────────────────────────────────────────────────────────

const GeneralTab: React.FC<{
  source: OverviewSource;
  orgId: string | null;
  orgName: string | null;
  onClose: () => void;
}> = ({ source, orgId, orgName, onClose }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState(source.name);
  useEffect(() => setName(source.name), [source.id, source.name]);

  const isIngestion = source.mode === 'ingestion';
  const isWebhook = source.sourceType === 'webhook';

  const { data: freqOptions } = useQuery({
    queryKey: ['syncFrequencyOptions'],
    queryFn: () => schedulesApi.getSyncFrequencyOptions(),
    enabled: isIngestion && !isWebhook,
    staleTime: Infinity,
  });
  const { data: schedules } = useQuery({
    queryKey: ['dataSourceSchedules'],
    queryFn: () => schedulesApi.getSchedules(),
    enabled: isIngestion && !isWebhook,
  });
  const currentFrequency = schedules?.schedules.find((s) => s.id === source.id)?.sync_frequency;

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['sourcesOverview'] });
    void queryClient.invalidateQueries({ queryKey: ['dataSourceSchedules'] });
  };

  const rename = useMutation({
    mutationFn: () => sourcesApi.updateSource(source.id, { name: name.trim() }),
    onSuccess: () => {
      invalidate();
      toast.success('Source renamed');
    },
    onError: (e) => toast.error(handleApiError(e) || 'Rename failed'),
  });
  const setToggle = useMutation({
    mutationFn: (changes: { syncEnabled?: boolean; aiAccessEnabled?: boolean }) =>
      sourcesApi.updateSource(source.id, changes),
    onSuccess: invalidate,
    onError: (e) => toast.error(handleApiError(e) || 'Update failed'),
  });
  const setFrequency = useMutation({
    mutationFn: (frequency: string) => schedulesApi.updateSyncFrequency(source.id, frequency),
    onSuccess: () => {
      invalidate();
      toast.success('Sync frequency updated');
    },
    onError: (e) => toast.error(handleApiError(e) || 'Update failed'),
  });

  return (
    <>
      <Field label="Source name" hint="How this source appears everywhere in Zamino and to AI tools.">
        <div className="flex gap-2">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-[10px] border border-input bg-background px-3 py-2 text-[13.5px] text-foreground"
          />
          {name.trim() !== source.name && name.trim() !== '' && (
            <button
              onClick={() => rename.mutate()}
              disabled={rename.isPending}
              className="rounded-[10px] bg-primary px-3.5 text-[13px] font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              Save
            </button>
          )}
        </div>
      </Field>

      <BusinessField source={source} orgId={orgId} orgName={orgName} onClose={onClose} />

      {isIngestion && !isWebhook && (
        <Field label="Sync frequency">
          <select
            value={currentFrequency ?? ''}
            onChange={(e) => setFrequency.mutate(e.target.value)}
            className="w-full rounded-[10px] border border-input bg-background px-3 py-2 text-[13.5px] text-foreground"
          >
            {!currentFrequency && <option value="">—</option>}
            {(freqOptions?.options ?? []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
      )}

      <div className="rounded-[10px] border border-border overflow-hidden mb-4">
        {isIngestion && (
          <ToggleRow
            title={isWebhook ? 'Receiving' : 'Syncing'}
            description="Pause to stop pulling new data. History stays queryable."
            checked={source.syncEnabled}
            onChange={(v) => setToggle.mutate({ syncEnabled: v })}
          />
        )}
        <ToggleRow
          title="AI access"
          description="Pause to hide this source from chat and external AI tools. Syncing continues."
          checked={source.aiAccessEnabled}
          onChange={(v) => setToggle.mutate({ aiAccessEnabled: v })}
        />
        <div className="flex items-center gap-3 px-3.5 py-3 border-t border-border">
          <div className="min-w-0">
            <div className="text-[13.5px] font-semibold text-foreground">Pulse monitoring</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Automatic anomaly watch on this source&apos;s activity.
            </div>
          </div>
          <button
            className="ml-auto inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-primary hover:underline flex-none"
            onClick={() => navigate('/pulse')}
          >
            {source.pulse === 'active' ? 'Active — manage' : 'Set up in Pulse'}
            <ExternalLink className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2.5 mt-4">
        <Fact k="Type" v={source.mode === 'ingestion' ? 'Stored copy (synced)' : 'On-demand (live)'} />
        <Fact k="Accounts" v={String(source.accounts.length)} />
        <Fact k="AI queries · 30d" v={source.counts.queries30d.toLocaleString()} />
        <Fact k="New records today" v={isIngestion ? `+${source.counts.recordsToday}` : '—'} />
      </div>
    </>
  );
};

// ── Business (move a source between businesses) ─────────────────────────

/**
 * Moving a source re-parents ALL of its history (transactions, accounts, emails,
 * semantics) to the target business in one atomic step — so it's confirm-gated
 * rather than a bare dropdown.
 */
const BusinessField: React.FC<{
  source: OverviewSource;
  orgId: string | null;
  orgName: string | null;
  onClose: () => void;
}> = ({ source, orgId, orgName, onClose }) => {
  const queryClient = useQueryClient();
  const { canManageOrganization } = usePermissions();
  const [target, setTarget] = useState('');

  const canMove = canManageOrganization();

  // The move target may be any org in the workspace, so this is the org list —
  // not the grant-filtered set the /sources overview is grouped by.
  const { data: orgs = [] } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => orgApi.listOrganizations(),
    enabled: canMove,
  });

  const move = useMutation({
    mutationFn: () => orgApi.moveSources([source.id], target),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sourcesOverview'] });
      void queryClient.invalidateQueries({ queryKey: ['organizations'] });
      void queryClient.invalidateQueries({ queryKey: ['orgSources'] });
      toast.success(`Moved ${source.name} to ${orgs.find((o) => o.id === target)?.name ?? 'the new business'}`);
      setTarget('');
      // The drawer holds a snapshot of the OLD business — reopen from the regrouped list.
      onClose();
    },
    onError: (e) => toast.error(handleApiError(e) || 'Move failed'),
  });

  if (!canMove) {
    return (
      <Field label="Business" hint="Only a workspace admin can move a source between businesses.">
        <div className="rounded-[10px] border border-border bg-muted px-3 py-2 text-[13.5px] text-muted-foreground">
          {orgName ?? '—'}
        </div>
      </Field>
    );
  }

  const others = orgs.filter((o) => o.id !== orgId);

  return (
    <Field
      label="Business"
      hint={
        others.length === 0
          ? 'This workspace has only one business.'
          : "Moves this source and all of its history — transactions, accounts and semantics — to the chosen business."
      }
    >
      <div className="flex gap-2">
        <select
          value={target || orgId || ''}
          onChange={(e) => setTarget(e.target.value === orgId ? '' : e.target.value)}
          disabled={others.length === 0 || move.isPending}
          className="w-full rounded-[10px] border border-input bg-background px-3 py-2 text-[13.5px] text-foreground disabled:opacity-60"
        >
          {orgId && <option value={orgId}>{orgName ?? 'Current business'}</option>}
          {others.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
        {target && (
          <button
            onClick={() => move.mutate()}
            disabled={move.isPending}
            className="whitespace-nowrap rounded-[10px] bg-primary px-3.5 text-[13px] font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {move.isPending ? 'Moving…' : 'Move'}
          </button>
        )}
      </div>
    </Field>
  );
};

// ── Setup (adapter-specific) ────────────────────────────────────────────

/**
 * The Setup tab is a router by source complexity: an inline config form for
 * simple key-value adapters, and a summary + deep link into the existing
 * full-page editors for the rich ones (webhook mapping, inbound email).
 */
const SetupTab: React.FC<{ source: OverviewSource }> = ({ source }) => {
  const kind = setupKind(source.sourceType);
  if (kind === 'webhook') return <WebhookSetupPanel source={source} />;
  if (kind === 'inbound_file') return <InboundFileSetupPanel source={source} />;
  return <ConfigSetupPanel source={source} />;
};

type ConfigValue = string | number | boolean | null;

interface ConfigSchemaField {
  type: 'text' | 'select' | 'boolean' | 'number' | 'date';
  label: string;
  description?: string;
  options?: Array<{ value: string; label: string }>;
  readOnly?: boolean;
}

const ConfigSetupPanel: React.FC<{ source: OverviewSource }> = ({ source }) => {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['dataSourceConfig', source.id],
    queryFn: () => integrationsApi.getDataSourceConfig(source.id),
  });

  const [values, setValues] = useState<Record<string, ConfigValue>>({});
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (data?.editableSettings) {
      setValues(data.editableSettings);
      setDirty(false);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () => integrationsApi.updateDataSourceConfig(source.id, values),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dataSourceConfig', source.id] });
      void queryClient.invalidateQueries({ queryKey: ['dataSourceSchedules'] });
      void queryClient.invalidateQueries({ queryKey: ['sourcesOverview'] });
      setDirty(false);
      toast.success('Settings saved');
    },
    onError: (e) => toast.error(handleApiError(e) || 'Save failed'),
  });

  if (isLoading) return <p className="text-[12.5px] text-muted-foreground">Loading settings…</p>;
  if (isError || !data) {
    return <p className="text-[12.5px] text-muted-foreground">Couldn&apos;t load settings — try reopening this drawer.</p>;
  }

  const fields = Object.entries(data.configSchema) as Array<[string, ConfigSchemaField]>;
  if (fields.length === 0) {
    return <p className="text-[12.5px] text-muted-foreground">This source has no adapter-specific settings.</p>;
  }

  const setField = (key: string, v: ConfigValue): void => {
    setValues((prev) => ({ ...prev, [key]: v }));
    setDirty(true);
  };

  return (
    <>
      <p className="text-[12.5px] text-muted-foreground mb-4">
        Connection settings specific to {integrationDisplay(source).name}. Sync frequency, pausing and AI
        access live in <span className="font-semibold text-foreground">General</span>.
      </p>
      {fields.map(([key, field]) => (
        <ConfigField key={key} fieldKey={key} field={field} value={values[key] ?? ''} onChange={setField} />
      ))}
      <div className="flex justify-end mt-1">
        <button
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate()}
          className="rounded-[10px] bg-primary px-4 py-2 text-[13px] font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {save.isPending ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </>
  );
};

const ConfigField: React.FC<{
  fieldKey: string;
  field: ConfigSchemaField;
  value: ConfigValue;
  onChange: (key: string, v: ConfigValue) => void;
}> = ({ fieldKey, field, value, onChange }) => {
  const base =
    'w-full rounded-[10px] border border-input bg-background px-3 py-2 text-[13.5px] text-foreground disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed';

  let control: React.ReactNode;
  switch (field.type) {
    case 'select':
      control = (
        <select
          className={base}
          value={String(value ?? '')}
          disabled={field.readOnly}
          onChange={(e) => onChange(fieldKey, e.target.value)}
        >
          <option value="">Select…</option>
          {field.options?.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
      break;
    case 'boolean':
      control = (
        <div className="flex items-center gap-2.5">
          <Toggle
            checked={value === true}
            disabled={field.readOnly}
            label={field.label}
            onChange={(v) => onChange(fieldKey, v)}
          />
          <span className="text-[13px] text-muted-foreground">{value === true ? 'Enabled' : 'Disabled'}</span>
        </div>
      );
      break;
    case 'number':
      control = (
        <input
          type="number"
          className={base}
          value={value == null || value === '' ? '' : String(value)}
          disabled={field.readOnly}
          onChange={(e) => onChange(fieldKey, e.target.value === '' ? null : Number(e.target.value))}
        />
      );
      break;
    case 'date': {
      let dateVal = '';
      if (value) {
        const d = new Date(value as string);
        dateVal = isNaN(d.getTime()) ? String(value) : d.toISOString().split('T')[0];
      }
      control = (
        <input
          type="date"
          className={base}
          value={dateVal}
          disabled={field.readOnly}
          onChange={(e) => onChange(fieldKey, e.target.value)}
        />
      );
      break;
    }
    default:
      control = (
        <input
          type="text"
          className={base}
          value={String(value ?? '')}
          disabled={field.readOnly}
          onChange={(e) => onChange(fieldKey, e.target.value)}
        />
      );
  }

  const hint = field.readOnly
    ? `Read-only.${field.description ? ` ${field.description}` : ''}`
    : field.description;

  return (
    <Field label={field.label} hint={hint}>
      {control}
    </Field>
  );
};

const WebhookSetupPanel: React.FC<{ source: OverviewSource }> = ({ source }) => {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['webhookMappingConfig', source.id],
    queryFn: () => integrationsApi.getWebhookMappingConfig(source.id),
  });

  const status = isLoading
    ? 'Loading…'
    : data?.hasMapping
      ? data.sampleCapturedAt
        ? `Configured · last payload ${new Date(data.sampleCapturedAt).toLocaleDateString()}`
        : 'Configured'
      : data?.samplePayload
        ? 'Sample received — not mapped yet'
        : 'Waiting for a first payload';

  return (
    <>
      <p className="text-[12.5px] text-muted-foreground mb-4">
        How incoming webhook payloads are turned into transactions.
      </p>
      {data?.webhookUrl && (
        <Field label="Webhook URL" hint="Point your sender at this URL. Treat it as a secret.">
          <MonoCopyRow value={data.webhookUrl} />
        </Field>
      )}
      <div className="flex items-center gap-3 rounded-[10px] border border-border p-3.5 mb-4">
        <div className="w-8 h-8 rounded-lg bg-primary/15 text-primary flex items-center justify-center flex-none">
          <Webhook className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <div className="text-[13.5px] font-semibold text-foreground">Field mapping</div>
          <div className="text-xs text-muted-foreground mt-0.5">{status}</div>
        </div>
        <span
          className={clsx(
            'ml-auto flex-none inline-flex items-center text-[11.5px] font-semibold rounded-full px-2.5 py-0.5',
            data?.hasMapping ? 'bg-success/15 text-success' : 'bg-warn/15 text-warn',
          )}
        >
          {data?.hasMapping ? 'Mapped' : 'Needs mapping'}
        </span>
      </div>
      <button
        onClick={() => navigate(`/integrations/webhook/${source.id}/edit`)}
        className="w-full inline-flex items-center justify-center gap-2 rounded-[10px] bg-primary px-4 py-2.5 text-[13px] font-medium text-primary-foreground hover:opacity-90"
      >
        Configure field mapping <ArrowRight className="w-4 h-4" />
      </button>
      <p className="text-[11.5px] text-muted-foreground mt-2.5 text-center">
        Opens the full mapping editor with the latest sample payload.
      </p>
    </>
  );
};

/** Deep-links to the full file-feed editor, which opens on its Review tab. */
const InboundFileSetupPanel: React.FC<{ source: OverviewSource }> = ({ source }) => {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['inboundFileSource', source.id],
    queryFn: () => integrationsApi.getInboundFileSource(source.id),
  });

  return (
    <>
      <p className="text-[12.5px] text-muted-foreground mb-4">
        Email recurring exports to this address — Zamino imports them with the approved mapping.
      </p>
      {data?.inboundAddress && (
        <Field label="Forwarding address" hint="Send (or auto-send) the export here.">
          <MonoCopyRow value={data.inboundAddress} />
        </Field>
      )}
      <div className="grid grid-cols-2 gap-2.5 mb-4">
        <Fact k="Allowed senders" v={isLoading ? '…' : String(data?.allowedSenders.length ?? 0)} />
        <Fact k="Status" v={isLoading ? '…' : data?.setupStatus === 'active' ? 'Active' : 'Paused'} />
      </div>
      {data?.summaryPurpose && (
        <Field label="Purpose">
          <div className="rounded-[10px] border border-border bg-muted px-3 py-2 text-[13px] text-muted-foreground">
            {data.summaryPurpose}
          </div>
        </Field>
      )}
      <button
        onClick={() => navigate(`/integrations/inbound-file/${source.id}/edit`)}
        className="w-full inline-flex items-center justify-center gap-2 rounded-[10px] bg-primary px-4 py-2.5 text-[13px] font-medium text-primary-foreground hover:opacity-90"
      >
        Manage file feed <ArrowRight className="w-4 h-4" />
      </button>
      <p className="text-[11.5px] text-muted-foreground mt-2.5 text-center">
        Review the mapping, edit allowed senders and see import activity.
      </p>
    </>
  );
};

const MonoCopyRow: React.FC<{ value: string }> = ({ value }) => {
  const [copied, setCopied] = useState(false);
  const copy = (): void => {
    navigator.clipboard?.writeText(value).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1400);
      },
      () => toast.error('Copy failed'),
    );
  };
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 min-w-0 rounded-[10px] border border-input bg-muted px-3 py-2 text-[12.5px] font-mono text-foreground overflow-x-auto whitespace-nowrap">
        {value}
      </div>
      <button
        onClick={copy}
        className="flex-none inline-flex items-center gap-1.5 rounded-[10px] border border-input bg-card px-3 py-2 text-[12.5px] font-medium text-foreground hover:bg-accent"
      >
        {copied ? (
          <>
            <Check className="w-3.5 h-3.5" /> Copied
          </>
        ) : (
          <>
            <Copy className="w-3.5 h-3.5" /> Copy
          </>
        )}
      </button>
    </div>
  );
};

// ── Accounts ────────────────────────────────────────────────────────────

const AccountsTab: React.FC<{ source: OverviewSource }> = ({ source }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const setTimezone = useMutation({
    mutationFn: ({ accountId, tz }: { accountId: string; tz: string }) =>
      sourcesApi.updateAccountTimezone(accountId, tz),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sourcesOverview'] });
      toast.success('Timezone updated');
    },
    onError: (e) => toast.error(handleApiError(e) || 'Update failed'),
  });

  return (
    <>
      <p className="text-[12.5px] text-muted-foreground mb-3.5">
        {source.accounts.length === 1
          ? 'This source has one account.'
          : `This source has ${source.accounts.length} accounts.`}
      </p>
      {source.accounts.map((a) => (
        <div key={a.id} className="rounded-[10px] border border-border p-3.5 mb-3">
          <div className="flex items-center gap-2 mb-2.5">
            <div className="min-w-0">
              <div className="text-[13.5px] font-semibold text-foreground truncate">
                {a.name ?? 'Account'}
              </div>
              <div className="text-[11.5px] text-muted-foreground">{a.type}</div>
            </div>
            <span
              className={clsx(
                'ml-auto inline-flex items-center gap-1 text-[11.5px] font-semibold rounded-full px-2.5 py-0.5',
                source.aiAccessEnabled ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground',
              )}
            >
              <Sparkles className="w-3 h-3" /> {source.aiAccessEnabled ? 'AI on' : 'AI paused'}
            </span>
          </div>
          <Field label="Business timezone" className="mb-0">
            <select
              value={a.businessTimezone ?? ''}
              onChange={(e) => setTimezone.mutate({ accountId: a.id, tz: e.target.value })}
              className="w-full rounded-[10px] border border-input bg-background px-3 py-2 text-[13.5px] text-foreground"
            >
              {!a.businessTimezone && <option value="">Not set</option>}
              {SUPPORTED_TIMEZONES.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
          </Field>
        </div>
      ))}

      <div className="flex items-start gap-3 rounded-[10px] border border-border bg-accent/50 p-3.5">
        <div className="w-8 h-8 rounded-lg bg-primary/15 text-primary flex items-center justify-center flex-none">
          <BookOpen className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <div className="text-[13.5px] font-semibold text-foreground">Knowledge wiki</div>
          <p className="text-[12.5px] text-muted-foreground mt-1 mb-2.5">
            What this data means — descriptions, quirks, naming conventions — is learned and kept up
            to date automatically by Zamino. The AI reads it with every query.
          </p>
          <button
            disabled={!source.wiki.pageId}
            title={source.wiki.pageId ? undefined : 'No wiki page yet for this source'}
            onClick={() => source.wiki.pageId && navigate(`/wiki/${source.wiki.pageId}`)}
            className={clsx(
              'inline-flex items-center gap-1.5 rounded-[10px] border border-input bg-card px-3 py-1.5 text-[13px] font-medium text-foreground',
              source.wiki.pageId ? 'hover:bg-accent' : 'opacity-50 cursor-not-allowed',
            )}
          >
            <BookOpen className="w-3.5 h-3.5" /> Open wiki
          </button>
          {source.wiki.exists && source.wiki.updatedAt && (
            <span className="text-[11.5px] text-muted-foreground ml-2.5">
              Updated {new Date(source.wiki.updatedAt).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>
    </>
  );
};

// ── Access ──────────────────────────────────────────────────────────────

const AccessTab: React.FC<{ source: OverviewSource }> = ({ source }) => {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');

  const { data: grants = [] } = useQuery({
    queryKey: ['sourceGrants', source.id],
    queryFn: () => accessApi.getSourceGrants(source.id),
  });
  const { data: orgUsers } = useQuery({
    queryKey: ['orgUsers'],
    queryFn: () => teamApi.listUsers(),
    enabled: adding,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['sourceGrants', source.id] });
    void queryClient.invalidateQueries({ queryKey: ['sourcesOverview'] });
  };

  const grant = useMutation({
    mutationFn: (userId: string) => accessApi.grantSource(source.id, userId),
    onSuccess: () => {
      invalidate();
      setAdding(false);
      setSelectedUser('');
      toast.success('Access granted');
    },
    onError: (e) => toast.error(handleApiError(e) || 'Grant failed'),
  });
  const revoke = useMutation({
    mutationFn: (userId: string) => accessApi.revokeSource(source.id, userId),
    onSuccess: () => {
      invalidate();
      toast.success('Access removed');
    },
    onError: (e) => toast.error(handleApiError(e) || 'Revoke failed'),
  });

  const grantedIds = new Set(grants.map((g) => g.user_id));
  const candidates = (orgUsers?.users ?? []).filter((u) => !grantedIds.has(u.id));

  return (
    <>
      <p className="text-[12.5px] text-muted-foreground mb-2">
        People who can query this source with AI. Managed per source — workspace role controls
        administration, not data visibility.
      </p>
      {grants.map((g) => {
        const displayName = `${g.first_name ?? ''} ${g.last_name ?? ''}`.trim() || g.email;
        const initials =
          `${g.first_name?.[0] ?? g.email[0]}${g.last_name?.[0] ?? ''}`.toUpperCase();
        return (
          <div key={g.id} className="flex items-center gap-3 py-2.5 border-b border-border last:border-b-0">
            <div className="w-[30px] h-[30px] rounded-full bg-primary/15 text-primary flex items-center justify-center text-[10.5px] font-bold flex-none">
              {initials}
            </div>
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-foreground truncate">{displayName}</div>
              <div className="text-[11.5px] text-muted-foreground truncate">{g.email}</div>
            </div>
            <span className="ml-auto text-[11.5px] text-muted-foreground bg-muted border border-border rounded-full px-2.5 py-0.5 capitalize">
              {g.role}
            </span>
            <button
              className="text-xs font-medium text-destructive hover:underline"
              onClick={() => revoke.mutate(g.user_id)}
            >
              Remove
            </button>
          </div>
        );
      })}

      {adding ? (
        <div className="flex items-center gap-2 mt-3.5">
          <select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            className="flex-1 rounded-[10px] border border-input bg-background px-3 py-2 text-[13.5px] text-foreground"
          >
            <option value="">Choose a workspace member…</option>
            {candidates.map((u) => (
              <option key={u.id} value={u.id}>
                {u.firstName} {u.lastName ?? ''} — {u.email}
              </option>
            ))}
          </select>
          <button
            disabled={!selectedUser || grant.isPending}
            onClick={() => grant.mutate(selectedUser)}
            className="rounded-[10px] bg-primary px-3.5 py-2 text-[13px] font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            Grant
          </button>
          <button
            onClick={() => setAdding(false)}
            className="rounded-[10px] border border-input bg-card px-3 py-2 text-[13px] text-foreground hover:bg-accent"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          className="mt-3.5 inline-flex items-center gap-1.5 rounded-[10px] border border-input bg-card px-3 py-1.5 text-[13px] font-medium text-foreground hover:bg-accent"
        >
          <Plus className="w-3.5 h-3.5" /> Grant access to someone
        </button>
      )}
    </>
  );
};

// ── Danger ──────────────────────────────────────────────────────────────

const DangerTab: React.FC<{ source: OverviewSource; onClose: () => void }> = ({ source, onClose }) => {
  const queryClient = useQueryClient();
  const [confirmText, setConfirmText] = useState('');

  const impact = useQuery({
    queryKey: ['sourceDeletionImpact', source.id],
    queryFn: () => sourcesApi.getDeletionImpact(source.id),
  });

  const impactItems: ImpactItem[] = impact.data
    ? [
        {
          label: 'Records permanently deleted',
          value: impact.data.records.toLocaleString(),
          emphasize: impact.data.records > 0,
        },
        {
          label: 'People who lose access',
          value: impact.data.usersLosingAccess,
          emphasize: impact.data.usersLosingAccess > 0,
        },
        { label: 'Semantic entries removed', value: impact.data.semanticEntries },
        { label: 'Wiki pages removed', value: impact.data.wikiPages },
        { label: 'Queries in the last 30 days', value: impact.data.queries30d },
      ]
    : [];

  const del = useMutation({
    mutationFn: () => sourcesApi.deleteSource(source.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sourcesOverview'] });
      void queryClient.invalidateQueries({ queryKey: ['dataSourceSchedules'] });
      toast.success(`${source.name} deleted`);
      onClose();
    },
    onError: (e) => toast.error(handleApiError(e) || 'Delete failed'),
  });

  return (
    <div className="rounded-[10px] border border-destructive/35 p-4">
      <div className="text-[13.5px] font-semibold text-destructive">Delete this source</div>
      <p className="text-[12.5px] text-muted-foreground mt-1.5 mb-3">
        Permanently removes the connection and all stored records, plus its wiki, semantics, and
        routines. People lose access immediately. This cannot be undone.
      </p>
      <DeletionImpactPreview
        items={impactItems}
        loading={impact.isLoading}
        error={impact.isError}
        className="mb-3"
      />
      <Field label="Type DELETE to confirm">
        <input
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="DELETE"
          className="w-full rounded-[10px] border border-input bg-background px-3 py-2 text-[13.5px] text-foreground"
        />
      </Field>
      <button
        disabled={confirmText !== 'DELETE' || del.isPending}
        onClick={() => del.mutate()}
        className="inline-flex items-center gap-1.5 rounded-[10px] bg-destructive px-3.5 py-2 text-[13px] font-semibold text-destructive-foreground disabled:opacity-50 hover:opacity-90"
      >
        <Trash2 className="w-3.5 h-3.5" /> Delete source
      </button>
    </div>
  );
};

// ── Shared bits ─────────────────────────────────────────────────────────

const Field: React.FC<{ label: string; hint?: string; className?: string; children: React.ReactNode }> = ({
  label,
  hint,
  className,
  children,
}) => (
  <div className={clsx('mb-4', className)}>
    <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
      {label}
    </label>
    {children}
    {hint && <p className="text-[11.5px] text-muted-foreground mt-1.5">{hint}</p>}
  </div>
);

const ToggleRow: React.FC<{
  title: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}> = ({ title, description, checked, onChange }) => (
  <div className="flex items-center gap-3 px-3.5 py-3 first:border-t-0 border-t border-border">
    <div className="min-w-0">
      <div className="text-[13.5px] font-semibold text-foreground">{title}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{description}</div>
    </div>
    <div className="ml-auto flex-none">
      <Toggle checked={checked} onChange={onChange} label={title} />
    </div>
  </div>
);

const Fact: React.FC<{ k: string; v: string }> = ({ k, v }) => (
  <div className="rounded-lg border border-border bg-background px-3 py-2">
    <div className="text-[9.5px] font-bold uppercase tracking-wider text-muted-foreground">{k}</div>
    <div className="text-[12.5px] font-semibold text-foreground mt-0.5">{v}</div>
  </div>
);

export default SourceSettingsDrawer;
